import { ButtonList } from '@dixa/lib-shared-habla';
import type { SearchConversationEnriched } from 'lib-search-types';
import { isResponseWithData } from 'lib-shared-auth';
import { Sentry, SentryTeam } from 'lib-shared-sentry';
import { ampli } from 'lib-shared-tracking';
import { MouseEventHandler, useCallback } from 'react';

import { conversationsApi } from 'apis/conversations';
import { appActions } from 'app/app.actions';
import { useNotifications } from 'hooks/useNotifications';
import { useAppDispatch } from 'utils/redux/useAppDispatch';

import { ConversationsTableRowMenuItemBaseProps } from '../conversationsTableRowMenuItem.types';

type ConversationsTableRowMenuCloseProps = {
  /** The conversation to close. */
  conversation: SearchConversationEnriched;
} & ConversationsTableRowMenuItemBaseProps;

/** Represents the component for closing the conversation. */
export const ConversationsTableRowMenuClose = ({
  conversation,
  toggleDropdown,
}: ConversationsTableRowMenuCloseProps): JSX.Element => {
  const dispatch = useAppDispatch();
  const notify = useNotifications();
  const { id: csid } = conversation;

  /** Handles closing the conversation. */
  const handleClick = useCallback<
    MouseEventHandler<HTMLButtonElement>
  >(async () => {
    toggleDropdown();

    const { isCancelled, isConfirmed, confirm } = await dispatch(
      appActions.showModal({
        modal: {
          type: 'app:confirm',
          arg: {
            title: 'Are you sure you want to close a conversation?',
            description: `This action will close conversation #${csid}.`,
            confirm: {
              variant: 'danger',
              label: 'Yes, close conversation',
              async: true,
              asyncLabel: 'Closing conversation...',
            },
          },
        },
      }),
    );

    if (isCancelled) {
      ampli.modalConversationCloseCancelClicked();
    }

    if (isConfirmed) {
      ampli.modalConversationCloseConfirmClicked({ csid });

      confirm(async () => {
        const response = await dispatch(
          conversationsApi.endpoints.updateConversationClose.initiate({ csid }),
        );

        if (isResponseWithData(response)) {
          ampli.conversationClosed({
            location: 'conversationsOverview',
            channel: conversation.initial_channel,
            language: conversation.language ?? undefined,
            direction: conversation.initial_direction,
          });

          notify({
            title: 'Conversation closed',
            text: `Conversation #${csid} was successfully closed`,
            icon: 'info',
            status: 'success',
            time: 3000,
          });
        } else {
          Sentry.captureException(response.error, {
            tags: {
              team: SentryTeam.Conversations,
            },
          });

          notify({
            title: 'Error while closing conversation',
            text: `Closing conversation ${csid} was not successful.`,
            icon: 'exclamation',
            status: 'warning',
          });
        }
      });
    }
  }, [
    csid,
    conversation.initial_channel,
    conversation.language,
    conversation.initial_direction,
    dispatch,
    toggleDropdown,
    notify,
  ]);

  return (
    <ButtonList.Item onClick={handleClick}>Close conversation</ButtonList.Item>
  );
};
